import { decrypt, EncryptedData, isUndefinedOrEmpty } from '@afterpay/utils'
import { Theme } from '@afterpaytouch/core'
import {
  ConvergenceRollout,
  ConvergenceUnavailable,
  COHORT_COOKIE_NAME,
  LAST_USED_EMAIL_DOMAIN_COOKIE_NAME,
  MERCHANT_ID_COOKIE_NAME,
  MERCHANT_ORDER_SOURCE,
  APT_USER_ID,
  mapRolloutToTheme,
  blackListedShadowAccountThemes,
  IN_APP_BROWSER_COOKIE_NAME,
} from '../../model'
import { parseCookies, setCookie } from 'nookies'
import { Cohort, Country, OrderSource } from '@afterpaytouch/portal-api'
import { getLocalStorageItem } from '@afterpay/utils/storage'
import { app } from '../../env'
import { isValidThemeValue } from '../../utils/convergence'
import { isInAppBrowser } from '../../utils/device'

// Prefer the explicit hasCashtag signal from the API. When absent (older API
// responses that still return cashtag), derive it from cashtag presence so
// cohorting keeps working through the backend rollout. Returns undefined only
// when the response carries no signal at all — getCohort already treats
// undefined and false the same. Drop the fallback once the backend has fully
// migrated and cashtag is removed.
export const deriveHasCashtag = (response: { hasCashtag?: boolean; cashtag?: string | null }): boolean | undefined => {
  if (typeof response.hasCashtag === 'boolean') {
    return response.hasCashtag
  }
  if (response.cashtag !== undefined) {
    return !isUndefinedOrEmpty(response.cashtag)
  }
  return undefined
}

const getCohort = (hasCashtag: boolean | undefined, existing: boolean, shadowAccountExists: boolean = false): Cohort => {
  switch (true) {
    case existing && !hasCashtag:
      return Cohort.APOnly
    case shadowAccountExists || (!existing && hasCashtag):
      return Cohort.CashOnly
    default:
      return Cohort.NetNew
  }
}

export const getEmailDomain = (email?: string): string => {
  const emailRegex = /^[^@\s]+@([^@\s]+\.[^@\s]+)$/
  const match = email?.match(emailRegex)
  return match != null ? match[1] : ''
}

const getCountryColdStartTheme = (country?: Country): Theme | undefined => {
  const countryThemeMap: Record<string, string> = {
    [Country.US]: app.COLD_START_THEME_US,
    [Country.CA]: app.COLD_START_THEME_CA,
    [Country.GB]: app.COLD_START_THEME_GB,
    [Country.AU]: app.COLD_START_THEME_AU,
    [Country.NZ]: app.COLD_START_THEME_NZ,
  }

  if (country && countryThemeMap[country] && isValidThemeValue(countryThemeMap[country])) {
    return countryThemeMap[country] as Theme
  }

  // Fallback to the original COLD_START_THEME for backwards compatibility
  if (isValidThemeValue(app.COLD_START_THEME)) {
    return app.COLD_START_THEME
  }

  return undefined
}

export const getCohortTheme = (rollout: ConvergenceRollout, shadowAccountExists: boolean, country?: Country): Theme => {
  const countryColdStartTheme = getCountryColdStartTheme(country)
  const rolloutTheme = mapRolloutToTheme[rollout]
  const defaultTheme = shadowAccountExists ? Theme.Cash : Theme.AP

  if (shadowAccountExists && blackListedShadowAccountThemes.includes(rolloutTheme)) {
    // default to cold start if shadow account exist and theme is blacklisted
    return countryColdStartTheme ?? defaultTheme
  }

  // prefer rollout theme over cold start theme, but fallback to default theme if neither exists
  return rolloutTheme ?? countryColdStartTheme ?? defaultTheme
}

export const evaluateCohort = (
  hasCashtag: boolean | undefined,
  existing: boolean,
  rollout: ConvergenceRollout = ConvergenceUnavailable.Unavailable,
  email?: string,
  shadowAccountExists = false,
  merchantId?: string,
  country?: Country,
  source?: OrderSource
): {
  cohort: Cohort
  theme: Theme
} => {
  const cohort = getCohort(hasCashtag, existing, shadowAccountExists)
  const emailDomain = getEmailDomain(email)
  const isIAB = isInAppBrowser()
  setCookie(null, COHORT_COOKIE_NAME, cohort, {
    path: '/',
    sameSite: 'none',
    secure: true,
    maxAge: 24 * 60 * 60, // 1 day
  })
  setCookie(null, LAST_USED_EMAIL_DOMAIN_COOKIE_NAME, emailDomain, {
    path: '/',
    sameSite: 'none',
    secure: true,
  })
  if (!isUndefinedOrEmpty(merchantId)) {
    setCookie(null, MERCHANT_ID_COOKIE_NAME, merchantId as string, {
      path: '/',
      sameSite: 'none',
      secure: true,
    })
  }
  if (!isUndefinedOrEmpty(source)) {
    setCookie(null, MERCHANT_ORDER_SOURCE, source as string, {
      path: '/',
      sameSite: 'none',
      secure: true,
    })
  }

  setCookie(null, IN_APP_BROWSER_COOKIE_NAME, isIAB.toString(), {
    path: '/',
    sameSite: 'none',
    secure: true,
  })

  const theme = getCohortTheme(rollout, shadowAccountExists, country)
  return { cohort, theme }
}

export const isCookieEnabled = (): boolean => {
  // setting a dumb cookie to check whether able to set and read the cookie value
  setCookie(null, 'testcookie', 'test', {
    path: '/',
    sameSite: 'none',
    secure: true,
  })

  const cookies = parseCookies()
  const isCookieEnabled = !!cookies.testcookie

  // delete dumb cookie after checking
  setCookie(null, 'testcookie', 'test', {
    path: '/',
    sameSite: 'none',
    secure: true,
    expires: new Date('Thu, 01 Jan 1970 00:00:01 GMT'),
  })

  return isCookieEnabled
}

export const tryGetStoredUUIDFromLocalStorage = (): string => {
  const uuidValue: EncryptedData | string | null = getLocalStorageItem(APT_USER_ID)
  if (typeof uuidValue === 'string') {
    return uuidValue
  }
  return decrypt(uuidValue) ?? ''
}
